import { Component, Inject, OnInit } from '@angular/core';
import { Airport } from '../models/airport.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatSelectChange, MatSnackBar } from '@angular/material';
import { OverviewService } from '../service/overview.service';
import { Fss } from '../models/fss.model';
import { formatPhoneString, undoPhoneFormat } from '../../util/phone';

@Component({
  selector: 'app-edit-operations',
  templateUrl: './edit-operations.component.html',
  styleUrls: ['./edit-operations.component.scss']
})
export class EditOperationsComponent implements OnInit {
  airport: Airport;
  origAirport: Airport;
  isLoading = true;
  formGroup: FormGroup;
  fssSelectId: '1';
  fsses: Fss[];
  selectedWindIndicator: string;
  selectedSegmentedCircle: string;
  selectedBeacon: string;

  constructor(private _matDialogRef: MatDialogRef<EditOperationsComponent>,
              public _snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data,
              private _overveiwService: OverviewService) {
    this.airport = data.airport || new Airport();
    this.origAirport = Object.assign(new Airport, data.airport);
    this.selectedWindIndicator = this.windIndicator;
    this.selectedSegmentedCircle = this.segmentedCircle;
    this.selectedBeacon = this.airport.beacon === true ? 'Yes' : 'No';
  }

  async ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this.close({cancelBtnClicked: true});
    });
    this.formGroup = new FormGroup({
      masterAirportTower: new FormControl(this.airport.masterAirportTower),
      fssName: new FormControl(this.airport.fssName),
      fssPhone: new FormControl(formatPhoneString(this.airport.fssPhone), [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      windIndicator: new FormControl(this.selectedWindIndicator),
      magVariation: new FormControl(this.airport.magVariation),
      magVariationEpochYear: new FormControl(this.airport.magVariationEpochYear, [Validators.min(1900), Validators.max(2050)]),
      segmentedCircle: new FormControl(this.selectedSegmentedCircle),
      beacon: new FormControl(this.selectedBeacon),
      beaconColor: new FormControl(this.airport.beaconColor)
    });
    this.isLoading = false;
    try {
      this.fsses = await this._overveiwService.getFsses();
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
  }

  get windIndicator(): string {
    if (this.airport.windIndicator && this.airport.windIndicatorLighted) {
      return 'Lighted';
    }
    return this.airport.windIndicator ? 'Yes' : 'No';
  }

  get segmentedCircle(): string {
    if (this.airport.segmentedCircle && this.airport.segmentedCircleLighted) {
      return 'Lighted';
    }
    return this.airport.segmentedCircle ? 'Yes' : 'No';
  }

  get beacon(): string {
    let beaconInfo: string;
    if (this.airport.beacon) {
      beaconInfo = 'Present';
      if (this.airport.beaconColor != null && this.airport.beaconColor.length > 0) {
        beaconInfo = beaconInfo + ' (' + this.airport.beaconColor + ')';
      }
      return beaconInfo;
    }
    return 'Not Present';
  }

  /**
   * formats passed in phone number string;
   * @param {string} field
   * @param {string} value
   * @return {string}
   */
  formatPhoneNumber (field: string, value: string): string {
    const NEW_NUMBER: string = formatPhoneString(value);
    this.formGroup.patchValue({[field]: NEW_NUMBER});
    return NEW_NUMBER;
  }

  /**
   * event triggered by keyup event on phone number or fax fields
   * @param event
   * @param {string} field
   * @param phoneInputObj
   */
  phoneFieldKeyUp (event, field: string, phoneInputObj) {
    const value: string = this.formGroup.get(field).value;
    const rawValue = undoPhoneFormat(value);
    const KEY = event.key;

    if (KEY === 'Backspace' && rawValue.length === 10 && phoneInputObj.selectionStart === rawValue.length) {
      this.formatPhoneNumber(field, value);
    } else if (phoneInputObj.selectionStart < value.length) {
    } else if (KEY === 'Backspace' || KEY === 'ArrowLeft' || KEY === 'ArrowRight') {
      return;
    } else {
      this.formatPhoneNumber(field, value);
    }
  }

  /**
   * triggered when phone number or fax field goes out of focus
   * @param {string} field
   */
  phoneFieldBlur(field: string) {
    const VALUE: string = this.formGroup.get(field).value;
    if (VALUE === '') {
      return;
    }
    this.formatPhoneNumber(field, VALUE);
  }

  getFssId(name: string): string {
    const index = this.fsses.findIndex(f => f.name === name);
    if (index !== -1) {
      return this.fsses[index].fssId;
    }
    return 'NONE';
  }

  /**
   * triggered when value inside fss dropdown changes; event.value contains fss string .
   * @param {MatSelectChange} event
   * @return {Promise<void>}
   */
  fssNameChange(event: MatSelectChange) {
    // if value property is empty return
    if (!event.value) { return; }
    if (event.value === this.airport.fssName) {
      this.formGroup.patchValue({fssName: this.airport.fssName || ''});
    }
  }

  /**
   * triggered by save button click event
   * @return {Promise<void>}
   */
  async save() {
    try {
      let result: any;
      this.airport.masterAirportTower = this.formGroup.value.masterAirportTower;
      this.airport.fssId = this.getFssId(this.formGroup.value.fssName);
      this.airport.fssPhone = this.formGroup.value.fssPhone;
      this.airport.magVariation = this.formGroup.value.magVariation;
      this.airport.magVariationEpochYear = this.formGroup.value.magVariationEpochYear;
      this.airport.windIndicator = (this.selectedWindIndicator === 'Lighted' || this.selectedWindIndicator === 'Yes');
      this.airport.windIndicatorLighted = this.selectedWindIndicator === 'Lighted';
      this.airport.segmentedCircle = (this.selectedSegmentedCircle === 'Lighted' || this.selectedSegmentedCircle === 'Yes');
      this.airport.segmentedCircleLighted = this.selectedSegmentedCircle === 'Lighted';
      this.airport.beacon = !this.selectedBeacon.includes('Not Present');
      this.airport.beaconColor = this.formGroup.value.beaconColor;

      result = await this._overveiwService.putIslandAirport(this.airport);
      this.airport = Airport.parse(result);
      this.close({cancelBtnClicked: false, success: true, airport: this.airport});
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false, error: e});
    }
  }

  /**
   * triggered by cancel button click event
   */
  cancel () {
    this.close({cancelBtnClicked: true});
  }

  /**
   * closes modal
   * @param data
   */
  close(data: any) {
    if (data && data.cancelBtnClicked) {
      Object.assign(this.airport, this.origAirport);
    }
    this._matDialogRef.close(data);
  }
}
